/**
 * The publish sequence, extracted from the mutation so it can be tested.
 *
 * Upload-then-finalize is the whole contract of the flow: the bytes must be in
 * S3 before finalize HEADs them for a size and re-reads them for a SHA256.
 * Swapping the order, or finalizing after a failed upload, publishes a catalog
 * row pointing at bytes that aren't there — a broken download link for every
 * user, discovered only when someone clicks it.
 *
 * This repo runs vitest with `environment: 'node'` and no jsdom, so a hook or
 * component test isn't available; pulling the sequence out as a plain function
 * over an injected client is the house pattern for making it assertable.
 */
import type {
  PublishReleaseInput,
  ReleaseUploadResponse,
  SoftwareRelease,
} from '../../types/downloads';

/** The two calls this flow needs — narrower than the whole apiService, so a
 * test supplies two spies rather than a mock of everything. */
export interface PublishReleaseClient {
  uploadSoftwareReleaseBinary(
    input: PublishReleaseInput,
    file: File,
    onProgress?: (percent: number) => void
  ): Promise<ReleaseUploadResponse>;
  finalizeSoftwareRelease(input: PublishReleaseInput): Promise<SoftwareRelease>;
}

export async function publishRelease(
  client: PublishReleaseClient,
  input: PublishReleaseInput,
  file: File,
  onProgress?: (percent: number) => void
): Promise<SoftwareRelease> {
  await client.uploadSoftwareReleaseBinary(input, file, onProgress);
  return client.finalizeSoftwareRelease(input);
}
